import React, { useMemo, useRef, useState } from 'react';
import { Box, Grid, IconButton, MenuItem, Select, Stack, Tooltip, Typography, Button } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronRight as ChevronRightIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
} from '@mui/icons-material';

import { useApiRequests } from 'hooks/useApiRequests';
import { Spinner, TwoOptionsDialog } from 'components';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { NoFaxesOverlay } from '../NoFaxesOverlay/NoFaxesOverlay';
import { MoreOptions } from '../MoreOptions/MoreOptions';
import RenameFileDialog from 'components/Dialogs/RenameFileDialog/RenameFileDialog';
import SendFaxDialog from '../SendFaxDialog/SendFaxDialog';
import { useFax } from 'context/fax';
import { useDialog } from 'hooks';
import { SentFaxDto } from 'types/fax';

import styles from './SentFaxes.module.scss';

type SortColumn = 'filename' | 'sentBy' | 'sentTo' | 'date';

type Props = {
  initialRowsPerPage?: number;
};

const rowsPerPageOptions = [5, 10, 25];

/* =====================
   Component
===================== */

export function SentFaxes({ initialRowsPerPage }: { initialRowsPerPage?: number }) {
  const { t } = useTranslation('settings');
  const tableRef = useRef<HTMLDivElement>(null);
  const { showDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const [sendOpen, setSendOpen] = useState(false);
  const [selectedFaxIds, setSelectedFaxIds] = useState<string[]>([]);
  const { getSentFaxes, downloadSentFax, sendFax, deleteSentFax, resendSentFax } = useApiRequests();
  const { faxNumber, sentFaxes, loadSentFaxes, loadingSent } = useFax();
  const { addOptimisticSentFax, scheduleSentFaxReconciliation } = useFax();

  const isInProgress = (status?: string) => {
    if (!status) return false;

    const s = status.toLowerCase();
    return s.includes('queue') || s.includes('convert') || s.includes('process') || s.includes('sending');
  };

  const getStatusClass = (status?: string) => {
    if (!status) return styles.queued;

    const s = status.toLowerCase();

    if (s.includes('deliver') || s === 'sent') return styles.delivered;
    if (s.includes('fail')) return styles.failed;
    if (s.includes('queue')) return styles.queued;
    if (s.includes('send') || s.includes('process') || s.includes('convert')) return styles.progress;

    return styles.unknown;
  };

  const formatFaxStatus = (status?: string) => {
    if (!status) return t('faxCommon.queued');

    const s = status.toLowerCase();

    if (s.includes('queue') || s.includes('convert')) return t('faxCommon.sending');
    if (s.includes('send') || s.includes('process')) return t('faxCommon.sending');
    if (s.includes('fail')) return t('faxCommon.failed');
    if (s.includes('deliver') || s === 'sent') return t('faxCommon.sent');

    return status;
  };

  const [sortConfig, setSortConfig] = useState<{
    column: SortColumn | null;
    order: 'asc' | 'desc' | 'none';
  }>({
    column: 'date',
    order: 'desc',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage ?? 10);

  // const normalizeStatus = (fax: any): string => {
  //   if (fax.Status && typeof fax.Status === 'string') {
  //     return fax.Status;
  //   }

  //   const code = fax.ResultCode ?? fax.Result;

  //   switch (code) {
  //     case 0:
  //       return 'Sent';
  //     case 1:
  //       return 'Queued';
  //     case 2:
  //       return 'Sending';
  //     case -1:
  //       return 'Failed';
  //     default:
  //       // SRFax portal-sent faxes usually mean success
  //       return 'Sent';
  //   }
  // };

  const getVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'queued':
      case 'sending':
        return 'info';
      case 'delivered':
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const reloadTimeout = useRef<NodeJS.Timeout | null>(null);

  /* =====================
     Sorting
  ===================== */

  const handleSort = (column: SortColumn) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        const nextOrder = prev.order === 'none' ? 'asc' : prev.order === 'asc' ? 'desc' : 'none';
        return { column, order: nextOrder };
      }
      return { column, order: 'asc' };
    });
  };

  const sortedFaxes = useMemo(() => {
    if (!sortConfig.column || sortConfig.order === 'none') {
      return sentFaxes;
    }

    return [...sentFaxes].sort((a, b) => {
      let aVal = '';
      let bVal = '';

      switch (sortConfig.column) {
        case 'filename':
          aVal = a.fileName.toLowerCase();
          bVal = b.fileName.toLowerCase();
          break;
        case 'sentBy':
          aVal = a.sentBy.toLowerCase();
          bVal = b.sentBy.toLowerCase();
          break;
        case 'sentTo':
          aVal = a.sentTo.toLowerCase();
          bVal = b.sentTo.toLowerCase();
          break;
        case 'date':
          return sortConfig.order === 'asc'
            ? (a.sentAtEpoch ?? 0) - (b.sentAtEpoch ?? 0)
            : (b.sentAtEpoch ?? 0) - (a.sentAtEpoch ?? 0);
      }

      return sortConfig.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [sentFaxes, sortConfig]);

  /* =====================
     Pagination
  ===================== */

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pagedFaxes = sortedFaxes.slice(startIndex, endIndex);

  const paginationText = `${startIndex + 1}-${Math.min(endIndex, sortedFaxes.length)} ${t('faxCommon.of')} ${
    sortedFaxes.length
  }`;

  const handleRowsPerPageChange = (event: SelectChangeEvent<string>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleFirstPageClick = () => setCurrentPage(1);
  const handlePreviousPageClick = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPageClick = () => setCurrentPage((prev) => (endIndex < sortedFaxes.length ? prev + 1 : prev));
  const handleLastPageClick = () => setCurrentPage(Math.ceil(sortedFaxes.length / rowsPerPage));

  const getArrow = (column: SortColumn) => {
    if (sortConfig.column !== column) return '';
    return sortConfig.order === 'asc' ? '▲' : sortConfig.order === 'desc' ? '▼' : '';
  };

  const formatPhoneNumber = (value?: string | null) => {
    if (!value) return '—';

    const digits = value.replace(/\D/g, '');

    // Handle leading country code "1"
    const normalized = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

    if (normalized.length !== 10) {
      return value;
    }

    return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
  };

  /* =====================
     Actions (Placeholders)
  ===================== */

  const handleDownload = async (fax: SentFaxDto) => {
    const blob = await downloadSentFax(fax.id);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    const filename = fax.fileName.toLowerCase().endsWith('.pdf') ? fax.fileName : `${fax.fileName}.pdf`;

    a.href = url;
    a.download = filename;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  };

  const handleDelete = (fax: SentFaxDto) => {
    const modal = showDialog(TwoOptionsDialog, {
      title: t('faxCommon.deleteFaxUpper'),
      text: (
        <Typography
          variant='body2'
          sx={{
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {t('faxCommon.deleteFaxQuestion', { fileName: fax.fileName })}
        </Typography>
      ),
      confirmText: t('faxCommon.delete'),
      onConfirm: async () => {
        try {
          modal.destroy();

          // ✅ ACTUAL DELETE CALL
          await deleteSentFax(fax.id);

          // ✅ Refresh list after deletion
          await loadSentFaxes();

          enqueueSnackbar(t('faxCommon.faxDeleted'), { variant: 'success' });
        } catch {
          enqueueSnackbar(t('faxCommon.failedToDeleteFax'), { variant: 'error' });
        }
      },
    });
  };

  const handleResend = async (fax: SentFaxDto) => {
    await resendSentFax(fax.id);

    enqueueSnackbar(t('faxCommon.faxResentSuccessfully'), { variant: 'success' });

    // Immediate refresh
    await loadSentFaxes();
  };

  const normalizeStatus = (status?: string) => {
    return typeof status === 'string' ? status.toLowerCase() : '';
  };

  const canDelete = (status?: string) => {
    const s = normalizeStatus(status);
    return s === 'sent' || s === 'delivered' || s === 'failed';
  };

  const canResend = (status?: string) => {
    const s = normalizeStatus(status);
    return s === 'sent' || s === 'delivered' || s === 'failed';
  };

  const canDownload = (_status?: string) => {
    return true;
  };

  React.useEffect(() => {
    setSelectedFaxIds((prev) => prev.filter((id) => sentFaxes.some((fax) => fax.id === id)));
  }, [sentFaxes]);

  const toggleFaxSelection = (fax: SentFaxDto) => {
    setSelectedFaxIds((prev) => (prev.includes(fax.id) ? prev.filter((id) => id !== fax.id) : [...prev, fax.id]));
  };

  const handleBulkDelete = () => {
    const selectedFaxes = sentFaxes.filter((fax) => selectedFaxIds.includes(fax.id));
    if (selectedFaxes.length === 0) {
      return;
    }

    const deletableFaxes = selectedFaxes.filter((fax) => canDelete(fax.status));
    const skippedCount = selectedFaxes.length - deletableFaxes.length;

    if (deletableFaxes.length === 0) {
      enqueueSnackbar(t('sentFaxes.selectedFaxesCannotBeDeletedCurrentStatus'), { variant: 'warning' });
      return;
    }

    const modal = showDialog(TwoOptionsDialog, {
      title: t('faxCommon.deleteFaxesUpper'),
      text: (
        <Typography variant='body2'>
          {t('sentFaxes.deleteSelectedFaxesQuestion', { count: deletableFaxes.length })}
          {skippedCount > 0 ? ` ${t('sentFaxes.selectedFaxesWillBeSkipped', { count: skippedCount })}` : ''}
        </Typography>
      ),
      confirmText: t('faxCommon.delete'),
      onConfirm: async () => {
        modal.destroy();

        let succeeded = 0;
        for (const fax of deletableFaxes) {
          try {
            await deleteSentFax(fax.id);
            succeeded += 1;
          } catch {
            // continue
          }
        }

        await loadSentFaxes();
        setSelectedFaxIds([]);

        if (succeeded === deletableFaxes.length) {
          enqueueSnackbar(t('sentFaxes.deletedFaxes', { count: succeeded }), { variant: 'success' });
        } else if (succeeded > 0) {
          enqueueSnackbar(t('sentFaxes.deletedSomeSelectedFaxes', { succeeded, total: deletableFaxes.length }), {
            variant: 'warning',
          });
        } else {
          enqueueSnackbar(t('faxCommon.failedToDeleteSelectedFaxes'), { variant: 'error' });
        }
      },
    });
  };

  /* =====================
     Render
  ===================== */

  return (
    <div className={styles.container}>
      {loadingSent && (
        <div className={styles.overlay}>
          <Spinner small />
        </div>
      )}
      {/* Faxes table */}
      {!loadingSent && Array.isArray(sentFaxes) && (
        <div className={styles.mainWrapper}>
          <div className={styles.main}>
            {/* Header */}
            <Stack className={styles.header} direction='row' justifyContent='space-between' alignItems='center'>
              <Typography variant='body2'>
                {t('faxCommon.myFaxNumber')} <b>{formatPhoneNumber(faxNumber)}</b>
              </Typography>

              <Stack direction='row' spacing={1} alignItems='center'>
                <Tooltip title={t('sentFaxes.refreshSentFaxes')}>
                  <span>
                    <IconButton onClick={loadSentFaxes} disabled={loadingSent} size='small'>
                      <RefreshIcon className={`${styles.refreshIcon} ${loadingSent ? styles.refreshing : ''}`} />
                    </IconButton>
                  </span>
                </Tooltip>

                {selectedFaxIds.length > 0 ? (
                  <Button
                    variant='contained'
                    color='error'
                    disabled={loadingSent}
                    onClick={handleBulkDelete}
                    sx={{ color: '#fff' }}
                  >
                    {t('faxCommon.delete')} ({selectedFaxIds.length})
                  </Button>
                ) : (
                  <Button variant='contained' disabled={loadingSent} onClick={() => setSendOpen(true)}>
                    {t('faxCommon.sendFax')}
                  </Button>
                )}
              </Stack>
            </Stack>

            {/* Table */}
            <div className={styles.table} ref={tableRef}>
              <Grid className={styles.tableHeader}>
                <Typography variant='subtitle2'>{t('faxCommon.status')}</Typography>

                <Typography variant='subtitle2'>{t('faxCommon.fileName')}</Typography>

                <Typography variant='subtitle2' className={styles.center}>
                  {t('sentFaxes.sentBy')}
                </Typography>

                <Typography variant='subtitle2' className={styles.center}>
                  {t('sentFaxes.sentTo')}
                </Typography>

                <Typography
                  variant='subtitle2'
                  onClick={() => handleSort('date')}
                  className={`${styles.dateCell} ${styles.sortableHeader}`}
                >
                  {t('sentFaxes.sentDate')} {getArrow('date')}
                </Typography>

                <Typography variant='subtitle2' className={styles.center}>
                  {t('faxCommon.pages')}
                </Typography>

                <Typography variant='subtitle2' />
              </Grid>

              <Box>
                {pagedFaxes.length === 0 ? (
                  <NoFaxesOverlay
                    title={t('sentFaxes.yourSentFolderIsEmpty')}
                    actionLabel={t('faxCommon.sendFax')}
                    onSendFaxClick={() => setSendOpen(true)}
                  />
                ) : (
                  <>
                    {pagedFaxes.map((fax) => (
                      <Grid
                        key={fax.id}
                        className={`${styles.tableItem} ${selectedFaxIds.includes(fax.id) ? styles.selected : ''}`}
                        onClick={() => toggleFaxSelection(fax)}
                      >
                        {/* Status */}
                        {/* Status */}
                        <Tooltip
                          title={
                            fax.status?.toLowerCase().includes('fail') && fax.failedMessage ? fax.failedMessage : ''
                          }
                          placement='top'
                          arrow
                          disableHoverListener={!fax.status?.toLowerCase().includes('fail') || !fax.failedMessage}
                        >
                          <Stack direction='row' alignItems='center' spacing={1}>
                            <span className={`${styles.statusDot} ${getStatusClass(fax.status)}`} />

                            <Typography variant='body1' fontWeight={600}>
                              {isInProgress(fax.status) ? (
                                <span className={styles.sendingText}>
                                  {formatFaxStatus(fax.status)}
                                  <span className={styles.dots} />
                                </span>
                              ) : (
                                formatFaxStatus(fax.status)
                              )}
                            </Typography>
                          </Stack>
                        </Tooltip>

                        {/* File Name */}
                        <Tooltip
                          title={fax.fileName}
                          placement='top'
                          componentsProps={{
                            tooltip: {
                              sx: {
                                maxWidth: 1200, // 👈 increase as needed
                                fontSize: '0.85rem',
                                whiteSpace: 'normal', // allow wrapping instead of clipping
                              },
                            },
                          }}
                        >
                          <Typography variant='body1' className={styles.fileName} noWrap>
                            {fax.fileName}
                          </Typography>
                        </Tooltip>

                        {/* Sent By */}
                        <Typography variant='body1' className={styles.center}>
                          {fax.sentBy}
                        </Typography>

                        {/* Sent To */}
                        <Typography variant='body1' className={styles.center}>
                          {formatPhoneNumber(fax.sentTo)}
                        </Typography>

                        {/* Sent Date */}
                        <Typography variant='body1' className={styles.dateCell}>
                          {fax.sentAt}
                        </Typography>

                        {/* Pages */}
                        <Typography variant='body1' className={styles.center}>
                          {fax.pages}
                        </Typography>

                        {/* Actions */}
                        <div onClick={(event) => event.stopPropagation()}>
                          <MoreOptions
                            item={fax}
                            onDownload={handleDownload}
                            onResend={handleResend}
                            onDelete={handleDelete}
                            downloadEnabled={canDownload(fax.status)}
                            resendEnabled={canResend(fax.status)}
                            deleteEnabled={canDelete(fax.status)}
                          />
                        </div>
                      </Grid>
                    ))}
                  </>
                )}
              </Box>
            </div>

            {/* Pagination */}
            <Stack className={styles.paginationRow}>
              <div className={styles.topRow}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography>{t('faxCommon.rowsPerPage')}</Typography>
                  <Select
                    value={rowsPerPage.toString()}
                    onChange={handleRowsPerPageChange}
                    IconComponent={ChevronRightIcon}
                  >
                    {rowsPerPageOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>

                <Typography>{paginationText}</Typography>
              </div>

              <Stack direction='row' spacing={1}>
                <IconButton onClick={handleFirstPageClick}>
                  <FirstPageIcon />
                </IconButton>
                <IconButton onClick={handlePreviousPageClick}>
                  <ArrowBackIcon />
                </IconButton>
                <IconButton onClick={handleNextPageClick}>
                  <ArrowForwardIcon />
                </IconButton>
                <IconButton onClick={handleLastPageClick}>
                  <LastPageIcon />
                </IconButton>
              </Stack>
            </Stack>
          </div>
        </div>
      )}
      <SendFaxDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onSend={async ({ files, faxNumber, subject }) => {
          try {
            // 1️⃣ Send to backend first
            await sendFax(files, faxNumber, subject);

            // 2️⃣ Only AFTER success + dialog close do we add optimistic row
            const tempId = `local-${Date.now()}`;

            addOptimisticSentFax({
              id: tempId,
              fileName: files.length === 1 ? files[0].name : `ZIP (${files.length} files)`,
              status: 'queued',
              statusCode: 1, // required by SentFaxDto
              sentBy: t('faxCommon.you'),
              sentTo: faxNumber,
              sentAt: t('faxCommon.justNow'),
              sentAtEpoch: Date.now(),
              pages: 0,
              failedMessage: null,
            });

            // 3️⃣ Schedule reconciliation (replace optimistic with real SRFax row)
            scheduleSentFaxReconciliation();

            // 4️⃣ Snackbar timing now matches sound + dialog close
            enqueueSnackbar(t('faxCommon.faxQueued'), { variant: 'success' });

            setSendOpen(false);
          } catch {
            enqueueSnackbar(t('faxCommon.failedToQueueFax'), { variant: 'error' });
            throw new Error();
          }
        }}
      />
    </div>
  );
}

export default SentFaxes;
